import React, { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { connect } from "react-redux";
import { Alert, Button, Col, Layout, Result, Row, Space, Spin, Table, Tooltip, Typography } from "antd";
import { ArrowLeftOutlined, CheckCircleOutlined, CloseCircleOutlined, EyeOutlined, WarningOutlined } from "@ant-design/icons";
import { InputCurrency } from "components";
import { formatMoney, maskCNPJ } from "utils";
import { getAllInternalTransferCompanies, getInternalTransferBalance, getInternalTransferCompanies } from "services/internal-transfer-service";
import { isMasterAdmin } from "./internal-transfer-access";
import InternalTransferParentScreen from "./internal-transfer-parent-screen";
import { InternalTransferCompanySelect } from "./internal-transfer-company-select";
import { useCompanyBalance, validateTransfer } from "./internal-transfer-balance";
import { CONFIRM, InternalTransferModal, RECEIVE } from "./internal-transfer-modal";

const { Content } = Layout;

const mapStateToProps = (state) => ({
    login: state.authReducer.loginReducer
});

const AVAILABILITY = {
    AVAILABLE: { icon: CheckCircleOutlined, color: "#52c41a" },
    PENDING: { icon: WarningOutlined, color: "#faad14" },
    BLOCKED: { icon: CloseCircleOutlined, color: "#ff4d4f" }
};

const AvailabilityIcon = ({ company }) => {
    const { icon: Icon, color } = AVAILABILITY[company.availability] || AVAILABILITY.BLOCKED;
    const reasons = company.eligible ? ["Disponível para transferência"] : (company.reasons || []);
    return (
        <Tooltip title={reasons.map((reason, index) => <div key={index}>{reason}</div>)}>
            <Icon style={{ color }} />
        </Tooltip>
    );
};

const InternalTransferForm = (props) => {
    const { companies, onTransfer } = props;
    const [toId, setToId] = useState(null);
    const [fromId, setFromId] = useState(null);
    const [amount, setAmount] = useState(0);
    const [validation, setValidation] = useState(null);
    const [toBalance, loadToBalance] = useCompanyBalance();
    const [fromBalance, loadFromBalance] = useCompanyBalance();

    const submit = () => {
        const error = validateTransfer({ fromId, toId, amount, fromBalance });
        if (error) {
            setValidation(error);
            return;
        }
        onTransfer({
            from: companies.find((company) => company.id === fromId),
            to: companies.find((company) => company.id === toId),
            amount
        }, () => {
            setAmount(0);
            loadToBalance(toId);
            loadFromBalance(fromId);
        });
    };

    return (
        <Row gutter={[16, 8]} align="top" style={{ marginTop: 16 }}>
            <Col xs={24} md={7}>
                <Typography.Text strong>Origem</Typography.Text>
                <InternalTransferCompanySelect
                    companies={companies}
                    excludeId={toId}
                    value={fromId}
                    onChange={(id) => { setFromId(id); setValidation(null); loadFromBalance(id); }}
                    balance={fromBalance}
                    placeholder="Selecione a origem"
                />
            </Col>
            <Col xs={24} md={7}>
                <Typography.Text strong>Destino</Typography.Text>
                <InternalTransferCompanySelect
                    companies={companies}
                    excludeId={fromId}
                    value={toId}
                    onChange={(id) => { setToId(id); setValidation(null); loadToBalance(id); }}
                    balance={toBalance}
                    placeholder="Selecione o destino"
                />
            </Col>
            <Col xs={24} md={6}>
                <Typography.Text strong>Valor</Typography.Text>
                <InputCurrency value={amount} onChange={(value) => { setAmount(value); setValidation(null); }} />
            </Col>
            <Col xs={24} md={4}>
                <div>&nbsp;</div>
                <Button type="primary" block onClick={submit}>Transferir</Button>
            </Col>
            {validation && (
                <Col span={24}>
                    <Alert type="warning" showIcon message={validation} />
                </Col>
            )}
        </Row>
    );
};

const InternalTransferGroupScreen = (props) => {
    const { parentId } = props;
    const navigate = useNavigate();
    const [data, setData] = useState([]);
    const [total, setTotal] = useState(0);
    const [loading, setLoading] = useState(false);
    const [page, setPage] = useState(1);
    const [pageSize, setPageSize] = useState(10);
    const [parentName, setParentName] = useState(null);
    const [notFound, setNotFound] = useState(false);
    const [balances, setBalances] = useState({});
    const [reload, setReload] = useState(0);
    const [eligibleCompanies, setEligibleCompanies] = useState([]);
    const [modal, setModal] = useState({ open: false });
    const readOnly = !!parentId;
    const basePath = parentId ? `/internal-transfer/parent/${parentId}` : "/internal-transfer";

    useEffect(() => {
        setBalances({});
        setParentName(null);
        setNotFound(false);
    }, [parentId]);

    useEffect(() => {
        const load = async () => {
            setLoading(true);
            try {
                const res = await getInternalTransferCompanies(parentId, page, pageSize);
                setNotFound(!!parentId && !!res.failed);
                setData(res.data);
                setTotal(res.total);
                const parent = res.data.find((company) => company.parent);
                if (parent) {
                    setParentName(parent.fantasyName);
                }
            } finally {
                setLoading(false);
            }
        };
        load();
    }, [parentId, page, pageSize, reload]);

    useEffect(() => {
        if (readOnly) {
            return;
        }
        const load = async () => {
            setEligibleCompanies(await getAllInternalTransferCompanies());
        };
        load();
    }, [readOnly, reload]);

    const onCloseModal = (changed) => {
        const afterClose = modal.afterClose;
        setModal({ open: false });
        if (changed) {
            setBalances({});
            setReload((value) => value + 1);
            if (afterClose) {
                afterClose();
            }
        }
    };

    const loadBalance = async (company) => {
        setBalances((prev) => ({ ...prev, [company.id]: { loading: true } }));
        const balance = await getInternalTransferBalance(company.id, parentId);
        setBalances((prev) => {
            const next = { ...prev };
            if (balance === null || balance === undefined) {
                delete next[company.id];
            } else {
                next[company.id] = { loading: false, balance };
            }
            return next;
        });
    };

    const renderBalance = (company) => {
        if (!company.eligible) {
            return "—";
        }
        const balance = balances[company.id];
        if (!balance) {
            return <EyeOutlined onClick={() => loadBalance(company)} />;
        }
        return balance.loading ? <Spin /> : formatMoney(balance.balance);
    };

    if (notFound) {
        return (
            <Content>
                <Button type="link" icon={<ArrowLeftOutlined />} style={{ padding: 0 }} onClick={() => navigate("/internal-transfer")}>
                    Voltar para matrizes
                </Button>
                <Result
                    status="warning"
                    title="Matriz não encontrada"
                    subTitle="A empresa informada não existe ou não é uma matriz com filiais."
                    extra={
                        <Button type="primary" onClick={() => navigate("/internal-transfer")}>
                            Voltar para matrizes
                        </Button>
                    }
                />
            </Content>
        );
    }

    return (
        <Content>
            {parentId ? (
                <Space direction="vertical">
                    <Button type="link" icon={<ArrowLeftOutlined />} style={{ padding: 0 }} onClick={() => navigate("/internal-transfer")}>
                        Voltar para matrizes
                    </Button>
                    <Typography.Text type="secondary">
                        {`Transferências do grupo${parentName ? ` ${parentName}` : ""}. Somente visualização.`}
                    </Typography.Text>
                </Space>
            ) : (
                <>
                    <Typography.Text type="secondary">
                        Transfira valores entre matriz e filiais de forma rápida.
                    </Typography.Text>
                    <InternalTransferForm
                        companies={eligibleCompanies}
                        onTransfer={(transfer, afterClose) => setModal({ open: true, mode: CONFIRM, transfer, afterClose })}
                    />
                </>
            )}
            <Table
                rowKey="id"
                style={{ marginTop: 16 }}
                dataSource={data}
                loading={loading}
                bordered
                scroll={{ y: "50vh", x: 900 }}
                onRow={(company) => ({ style: company.eligible ? undefined : { opacity: 0.5 } })}
                pagination={{
                    current: page,
                    total,
                    pageSize,
                    showTotal: (total, range) => `${range[1]} de ${total}`,
                    disabled: loading,
                    showSizeChanger: true,
                    onChange: (newPage, newPageSize) => {
                        setPage(newPageSize !== pageSize ? 1 : newPage);
                        setPageSize(newPageSize);
                    }
                }}
            >
                <Table.Column
                    title="Nome Fantasia"
                    dataIndex="fantasyName"
                    key="fantasyName"
                    render={(value, company) => (
                        <Space>
                            <AvailabilityIcon company={company} />
                            {value}
                        </Space>
                    )}
                />
                <Table.Column width={200} align="center" title="CNPJ" dataIndex="cnpj" key="cnpj" render={(value) => (value ? maskCNPJ(value) : "")} />
                <Table.Column
                    width={180}
                    align="center"
                    title="Saldo atual"
                    key="balance"
                    render={(_, company) => renderBalance(company)}
                />
                <Table.Column
                    width={150}
                    align="center"
                    title="Extrato"
                    key="statement"
                    render={(_, company) => (
                        <Button type="link" onClick={() => navigate(`${basePath}/${company.id}`)}>
                            Visualizar
                        </Button>
                    )}
                />
                {!readOnly && (
                    <Table.Column
                        width={200}
                        align="center"
                        title="Receber transferência"
                        key="receive"
                        render={(_, company) => (
                            <Button type="link" disabled={!company.eligible} onClick={() => setModal({ open: true, mode: RECEIVE, company })}>
                                Receber
                            </Button>
                        )}
                    />
                )}
            </Table>
            <InternalTransferModal
                open={modal.open}
                mode={modal.mode}
                company={modal.company}
                transfer={modal.transfer}
                companies={eligibleCompanies}
                onClose={onCloseModal}
            />
        </Content>
    );
};

const InternalTransferScreen = (props) => {
    const { login } = props;
    const { parentId } = useParams();

    if (isMasterAdmin(login) && !parentId) {
        return <InternalTransferParentScreen />;
    }

    return <InternalTransferGroupScreen parentId={isMasterAdmin(login) ? parentId : undefined} />;
};

export default connect(mapStateToProps, {})(InternalTransferScreen);
